import type { SelectOption } from '../select';
import type { PhoneCountryCode } from './phone-mask';
import { Phone } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
// Sibling shared/ui components — import directly (not via the @/shared/ui
// barrel) since this component lives inside that barrel and would otherwise
// create a circular import.
import { COUNTRY_OPTIONS } from '../country-select';
import { Input } from '../input';
import { Select } from '../select';
import css from './phone-input.module.scss';
import {
  getMaxPhoneNationalLength,
  getPhoneCallingCode,
  inferPhoneCountry,
  isPhoneCountryCode,
  localPhoneFromValue,
  phoneValueFromCountryAndLocalInput,
  phoneValueUsesCountry,
  replacePhoneCountry,
  sortPhoneCountryCodes,
} from './phone-mask';

type PhoneInputProps = {
  id: string;
  name: string;
  onBlur?: () => void;
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
};

const labelByCountryCode = new Map(
  COUNTRY_OPTIONS.map((option) => [option.value, option.label])
);

const phoneCountryOptions: SelectOption[] = sortPhoneCountryCodes(
  COUNTRY_OPTIONS.map((option) => option.value).filter(isPhoneCountryCode)
).map((countryCode) => {
  const callingCode = getPhoneCallingCode(countryCode);

  return {
    label: `${labelByCountryCode.get(countryCode) ?? countryCode} +${callingCode}`,
    content: (
      <span className={css.countryOption}>
        <span>{labelByCountryCode.get(countryCode) ?? countryCode}</span>
        <span className={css.callingCode}>+{callingCode}</span>
      </span>
    ),
    value: countryCode,
  };
});

export const PhoneInput = ({
  id,
  name,
  onBlur,
  onChange,
  placeholder = 'Enter phone number',
  value,
}: PhoneInputProps) => {
  const [countryCode, setCountryCode] = useState<PhoneCountryCode>(() =>
    inferPhoneCountry(value)
  );

  useEffect(() => {
    if (phoneValueUsesCountry(value, countryCode)) return;
    setCountryCode(inferPhoneCountry(value));
  }, [countryCode, value]);

  const selectedCallingCode = getPhoneCallingCode(countryCode);
  const maxLocalDigits = getMaxPhoneNationalLength(countryCode);
  const localValue = useMemo(
    () => localPhoneFromValue(value, countryCode),
    [countryCode, value]
  );

  const handleCountryChange = (nextValue: string) => {
    if (!isPhoneCountryCode(nextValue)) return;

    onChange(replacePhoneCountry(value, countryCode, nextValue));
    setCountryCode(nextValue);
  };

  return (
    <div className={css.root}>
      <Select
        aria-label="Phone country"
        className={css.countrySelect}
        dropdownMinWidth={260}
        onChange={(event) => handleCountryChange(event.target.value)}
        options={phoneCountryOptions}
        placeholder="+"
        searchable
        searchPlaceholder="Search country..."
        usePortal
        value={countryCode}
      />
      <Input
        autoComplete="tel"
        className={css.phoneInput}
        id={id}
        inputMode="tel"
        leadingIcon={<Phone style={{ height: 14, width: 14 }} />}
        name={name}
        onBlur={onBlur}
        onChange={(e) =>
          onChange(
            phoneValueFromCountryAndLocalInput(countryCode, e.target.value)
          )
        }
        maxLength={maxLocalDigits + Math.floor((maxLocalDigits - 1) / 3)}
        placeholder={`${placeholder} (+${selectedCallingCode})`}
        type="tel"
        value={localValue}
      />
    </div>
  );
};
